import { faImage, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	ISSUE_ATTACHMENT_MAX_PER_MESSAGE,
	ISSUE_IMAGE_ACCEPT,
	ISSUE_IMAGE_MAX_BYTES,
	isIssueImageContentType,
	type IssueAttachmentModel,
} from '@supply-mover-2-0/shared';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../providers/TranslationProvider';
import { cn } from '../../utils/cn';

/** One image chosen for a message, while it uploads and after. */
export interface PendingIssueImageModel {
	key: string;
	name: string;
	/** Local preview (object URL) — never sent anywhere. */
	previewUrl: string;
	status: 'uploading' | 'done' | 'error';
	/** Set once the upload has finished. */
	fileId: number | null;
	error: string | null;
}

interface IssueImagePickerProps {
	value: Array<PendingIssueImageModel>;
	onChange: (
		update: (
			previous: Array<PendingIssueImageModel>,
		) => Array<PendingIssueImageModel>,
	) => void;
	/**
	 * Stores one image and returns its file id. The CRM uploads to a presigned
	 * storage URL; the storefront posts it to its own API.
	 */
	upload: (file: File) => Promise<IssueAttachmentModel>;
	disabled?: boolean;
	className?: string;
}

/** The uploaded file ids of the finished images, for the message payload. */
export function issueImageFileIds(
	images: Array<PendingIssueImageModel>,
): Array<number> {
	return images
		.map((image) => image.fileId)
		.filter((id): id is number => id != null);
}

/** Whether any chosen image is still uploading (the send button waits). */
export function issueImagesUploading(
	images: Array<PendingIssueImageModel>,
): boolean {
	return images.some((image) => image.status === 'uploading');
}

let keySequence = 0;

/**
 * PD-16475 — "Attach images" for an issue message (new issue, note,
 * Conversation reply, storefront request or reply). Each image uploads as
 * soon as it is chosen and shows as a thumbnail that can be removed; the
 * message then sends the finished file ids. On a phone the browser's picker
 * also offers the camera.
 */
export function IssueImagePicker({
	value,
	onChange,
	upload,
	disabled = false,
	className,
}: IssueImagePickerProps): React.JSX.Element {
	const { t } = useTranslation();
	const inputRef = useRef<HTMLInputElement>(null);
	const [notice, setNotice] = useState<string | null>(null);

	// Object URLs hold the image in memory until revoked; release every one
	// this picker created when it goes away.
	const previewsRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		const previews = previewsRef.current;

		return (): void => {
			previews.forEach((url) => {
				URL.revokeObjectURL(url);
			});
		};
	}, []);

	const updateImage = (
		key: string,
		patch: Partial<PendingIssueImageModel>,
	): void => {
		onChange((previous) =>
			previous.map((image) =>
				image.key === key ? { ...image, ...patch } : image,
			),
		);
	};

	const handleFiles = (files: FileList | null): void => {
		if (files == null) {
			return;
		}

		setNotice(null);
		const room = ISSUE_ATTACHMENT_MAX_PER_MESSAGE - value.length;
		const chosen = Array.from(files);

		if (chosen.length > room) {
			setNotice(
				t('You can attach up to {{max}} images.', {
					max: String(ISSUE_ATTACHMENT_MAX_PER_MESSAGE),
				}),
			);
		}

		for (const file of chosen.slice(0, Math.max(room, 0))) {
			if (!isIssueImageContentType(file.type)) {
				setNotice(t('Only JPEG, PNG, GIF or WebP images can be attached.'));
				continue;
			}

			if (file.size > ISSUE_IMAGE_MAX_BYTES) {
				setNotice(t('Images must be 10 MB or smaller.'));
				continue;
			}

			keySequence += 1;
			const key = `img-${keySequence}`;
			const previewUrl = URL.createObjectURL(file);
			previewsRef.current.add(previewUrl);

			onChange((previous) => [
				...previous,
				{
					key,
					name: file.name,
					previewUrl,
					status: 'uploading',
					fileId: null,
					error: null,
				},
			]);

			upload(file)
				.then((result) => {
					updateImage(key, { status: 'done', fileId: result.fileId });
				})
				.catch((error: unknown) => {
					const message =
						error instanceof Error && error.message.length > 0
							? error.message
							: t('Upload failed');

					updateImage(key, { status: 'error', error: message });
					setNotice(
						t('{{name}} could not be uploaded: {{message}}', {
							name: file.name,
							message,
						}),
					);
				});
		}
	};

	const remove = (key: string): void => {
		onChange((previous) => {
			const removed = previous.find((image) => image.key === key);

			if (removed != null) {
				URL.revokeObjectURL(removed.previewUrl);
				previewsRef.current.delete(removed.previewUrl);
			}

			return previous.filter((image) => image.key !== key);
		});
	};

	const atLimit = value.length >= ISSUE_ATTACHMENT_MAX_PER_MESSAGE;

	return (
		<div className={cn('space-y-2', className)}>
			{value.length > 0 && (
				<div className="flex flex-wrap gap-2">
					{value.map((image) => (
						<div
							key={image.key}
							className={cn(
								'relative h-16 w-16 overflow-hidden rounded-md border',
								image.status === 'error' ? 'border-danger' : 'border-default',
							)}
						>
							<img
								src={image.previewUrl}
								alt={t(image.name)}
								className={cn(
									'h-full w-full object-cover',
									image.status === 'uploading' ? 'opacity-50' : '',
								)}
							/>
							{image.status === 'uploading' && (
								<span className="absolute inset-x-0 bottom-0 bg-scrim py-0.5 text-center text-[10px] text-on-fill">
									{t('Uploading...')}
								</span>
							)}
							{image.status === 'error' && (
								<span className="absolute inset-x-0 bottom-0 bg-danger-solid py-0.5 text-center text-[10px] text-on-fill">
									{t('Failed')}
								</span>
							)}
							<button
								type="button"
								onClick={() => {
									remove(image.key);
								}}
								disabled={disabled}
								className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-scrim text-on-fill"
								aria-label={t('Remove image {{name}}', { name: image.name })}
							>
								<FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
							</button>
						</div>
					))}
				</div>
			)}

			<button
				type="button"
				onClick={() => {
					inputRef.current?.click();
				}}
				disabled={disabled || atLimit}
				className="inline-flex items-center gap-2 rounded-md border border-default px-3 py-1.5 text-sm text-default hover:bg-subtle disabled:cursor-not-allowed disabled:opacity-50"
			>
				<FontAwesomeIcon icon={faImage} className="h-4 w-4" />
				{t('Attach images')}
			</button>
			<input
				ref={inputRef}
				type="file"
				accept={ISSUE_IMAGE_ACCEPT}
				multiple
				className="hidden"
				onChange={(event) => {
					handleFiles(event.target.files);
					// Let the same file be picked again after a removal.
					event.target.value = '';
				}}
			/>
			{notice != null && <p className="text-xs text-danger">{notice}</p>}
		</div>
	);
}
